import forge from 'node-forge';

/**
 * Parse a PEM-encoded X.509 certificate and extract its expiration date.
 * @param {string} pemText - PEM certificate content (-----BEGIN CERTIFICATE----- ... -----END CERTIFICATE-----)
 * @returns {{ expiration: Date, expirationStr: string }} Parsed expiration date and formatted string
 * @throws Error if certificate is invalid or expired
 */
export function parseCertificate(pemText) {
  if (!pemText || typeof pemText !== 'string') {
    throw new Error('Conteúdo do certificado vazio');
  }

  if (!pemText.includes('-----BEGIN CERTIFICATE-----')) {
    throw new Error('Formato de certificado inválido. Esperado arquivo PEM (.crt).');
  }

  let cert;
  try {
    cert = forge.pki.certificateFromPem(pemText);
  } catch (e) {
    throw new Error('Certificado X.509 inválido: formato PEM malformado.');
  }

  if (!cert.validity || !cert.validity.notAfter) {
    throw new Error('Não foi possível extrair a data de expiração do certificado.');
  }

  const expiration = cert.validity.notAfter;
  const now = new Date();

  if (expiration <= now) {
    const dateStr = expiration.toISOString().slice(0, 10);
    throw new Error(`Certificado expirado em ${dateStr}.`);
  }

  const expirationStr = expiration.toISOString().slice(0, 10);
  return { expiration, expirationStr };
}

/**
 * Validate a PEM-encoded private key format.
 * @param {string} pemText - PEM private key content
 * @returns {{ valid: boolean }} Whether the key has valid PEM markers
 * @throws Error if key format is invalid
 */
export function validatePrivateKey(pemText) {
  if (!pemText || typeof pemText !== 'string') {
    throw new Error('Conteúdo da chave privada vazio');
  }

  const hasBegin = pemText.includes('-----BEGIN PRIVATE KEY-----')
    || pemText.includes('-----BEGIN RSA PRIVATE KEY-----');
  const hasEnd = pemText.includes('-----END PRIVATE KEY-----')
    || pemText.includes('-----END RSA PRIVATE KEY-----');

  if (!hasBegin || !hasEnd) {
    throw new Error('Formato de chave privada inválido. Esperado arquivo PEM (.key).');
  }

  return { valid: true };
}

/**
 * Read a File object as text using FileReader.
 * @param {File} file - The file to read
 * @returns {Promise<string>} File content as text
 */
export function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = () => reject(new Error('Erro ao ler o arquivo'));
    reader.readAsText(file);
  });
}
